import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> = {
  data: T | null;
  error: string | null;
  detail?: string;
  loading: boolean;
  reload: () => void;
};

/**
 * Fetch-on-mount with a manual reload. `deps` re-runs the loader; the ref guard
 * keeps a slow response from overwriting a newer one.
 */
export function useApi<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    loader()
      .then((value) => {
        if (generation.current !== mine) return;
        setData(value);
        setError(null);
      })
      .catch((err: unknown) => {
        if (generation.current !== mine) return;
        const e = err as { message?: string; detail?: string };
        setError(e.message ?? 'Something went wrong');
        setDetail(e.detail);
      })
      .finally(() => {
        if (generation.current === mine) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, detail, loading, reload };
}

/** Persist a small piece of UI state per browser, tolerating blocked storage. */
export function useLocalState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private window or storage disabled -- state still works in-session */
      }
    },
    [key],
  );

  return [value, update];
}

export type LiveEvent = {
  type: string;
  gameId?: string | null;
  headline?: string;
  detail?: string | null;
  severity?: number;
  at: string;
};

/**
 * Subscribe to the server's event stream.
 *
 * The browser reconnects on its own when the connection drops, so this only
 * has to hand events to the caller and report whether the link is up. Pass
 * `onEvent` to react to a specific change -- typically by reloading a panel.
 */
export function useStream(onEvent?: (event: LiveEvent) => void): {
  connected: boolean;
  last: LiveEvent | null;
} {
  const [connected, setConnected] = useState(false);
  const [last, setLast] = useState<LiveEvent | null>(null);
  // Keeping the callback in a ref means a new inline function on every render
  // does not tear down and rebuild the connection.
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    const source = new EventSource('/api/stream');

    const receive = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as LiveEvent;
        const event = { ...data, type: e.type };
        setLast(event);
        handler.current?.(event);
      } catch {
        /* a frame we cannot parse is not worth breaking the stream over */
      }
    };

    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);
    for (const type of ['line_move', 'model_move', 'lines', 'weather', 'roof', 'injury', 'bets_graded', 'graded', 'live_stats', 'picks_locked', 'model_line']) {
      source.addEventListener(type, receive as EventListener);
    }
    source.addEventListener('hello', () => setConnected(true));

    return () => source.close();
  }, []);

  return { connected, last };
}
